import React from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Stack,
  Select,
  MenuItem,
  Pagination,
  useTheme,
  useMediaQuery,
} from "@mui/material";

const ReusableTable = ({
  columns = [],
  rows = [],
  loading = false,
  error = null,
  LoaderComponent,

  page = 0,
  rowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 15, 25, 50, 100],
  totalPages = 0,
  totalElements = 0,
  onPageChange,
  onRowsPerPageChange,

  getRowId = (row) => row?.id ?? row?._id ?? row?.key,
  onRowClick,
  rowClickable,

  containerSx,
  tableSx,
  headerCellSx,
  cellSx,
  stickyHeader = true,
  size,
  noDataText = "No Data found.",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const resolvedSize = size || (isMobile ? "small" : "medium");
  const clickable = rowClickable ?? Boolean(onRowClick);

  const defaultContainerSx = {
    maxHeight: isMobile ? "60vh" : "none",
    overflow: "auto",
    width: "100%",
  };

  const defaultTableSx = {
    minWidth: isMobile ? "100%" : "650px",
    "& .MuiTableCell-root": {
      fontSize: isMobile ? "12px" : "14px",
      padding: isMobile ? "8px" : "12px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: isMobile ? "100px" : "200px",
    },
  };

  const defaultHeaderCellSx = {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    position: "sticky",
    top: 0,
    zIndex: 1,
    fontSize: isMobile ? "12px" : "14px",
    padding: isMobile ? "8px" : "12px",
  };

  const mergedContainerSx = { ...defaultContainerSx, ...containerSx };
  const mergedTableSx = { ...defaultTableSx, ...tableSx };
  const mergedHeaderCellSx = { ...defaultHeaderCellSx, ...headerCellSx };
  const mergedCellSx = { ...(defaultTableSx["& .MuiTableCell-root"] || {}), ...cellSx };

  return (
    <Box>
      <TableContainer component={Paper} sx={mergedContainerSx}>
        <Table stickyHeader={stickyHeader} sx={mergedTableSx} size={resolvedSize}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id || col.label}
                  align={col.align || "left"}
                  sx={{ ...mergedHeaderCellSx, ...(col.headerSx || {}) }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
                    {LoaderComponent ? <LoaderComponent /> : <Typography>Loading…</Typography>}
                  </Box>
                </TableCell>
              </TableRow>
            ) : !rows || rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
                    <Typography variant="subtitle1" color="textSecondary">
                      {error || noDataText}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => {
                const rowId = getRowId(row) ?? idx;
                return (
                  <TableRow
                    key={rowId}
                    id={`row-${rowId}`}
                  
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      ...(clickable
                        ? {
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                          }
                        : {}),
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    {columns.map((col) => {
                      const content =
                        typeof col.render === "function"
                          ? col.render(row)
                          : col.field
                          ? row[col.field]
                          : null;
                      return (
                        <TableCell
                          key={col.id || col.label}
                          align={col.align || "left"}
                          sx={{ ...mergedCellSx, ...(col.cellSx || {}) }}
                        >
                          {content}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalElements > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "12px 16px",
            borderTop: "1px solid #f0f0f0",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 0,
          }}
        >
          <Stack direction="row" justifyContent="flex-end" alignItems="center" flexWrap="wrap" spacing={2}>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" sx={{ fontSize: "12px", marginRight: "8px" }}>
                Rows per page:
              </Typography>
              <Select
                id="rows-per-page-select"
                name="rowsPerPage"
                value={rowsPerPage}
                onChange={(e) => onRowsPerPageChange && onRowsPerPageChange(e)}
                size="small"
                sx={{
                  height: isMobile ? "32px" : "36px",
                  "& .MuiSelect-select": {
                    padding: isMobile ? "8px 32px 8px 12px" : "10px 32px 10px 12px",
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem 
                    key={option} 
                    value={option}
                    id={`rows-per-page-option-${option}`}
                    name={`rowsPerPageOption${option}`}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Pagination 
              count={totalPages}
              page={(page ?? 0) + 1}
              onChange={(e, value) => onPageChange && onPageChange(value - 1)}
              showFirstButton
              showLastButton
              size={isMobile ? "small" : "medium"}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              id="table-pagination"
              name="tablePagination"
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ReusableTable;